import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const FooterContainer = styled.footer`
  background: #333;
  color: white;
  padding: 3rem 2rem 1rem;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem 1rem;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
`;

const FooterSection = styled.div`
  h3 {
    color: #ff6b6b;
    margin-bottom: 1rem;
    font-size: 1.3rem;
    
    @media (max-width: 768px) {
      font-size: 1.2rem;
      margin-bottom: 0.8rem;
    }
  }
  
  p {
    line-height: 1.6;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      font-size: 0.95rem;
      line-height: 1.5;
      margin-bottom: 0.8rem;
    }
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    gap: 0.4rem;
  }
`;

const FooterLink = styled(motion.a)`
  color: white;
  text-decoration: none;
  transition: color 0.3s ease;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    padding: 0.3rem 0;
    
    &.contact-link {
      font-weight: bold;
      
      &::after {
        content: " Us";
      }
    }
  }
  
  &:hover {
    color: #ff6b6b;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.8rem;
    margin-top: 0.8rem;
    justify-content: flex-start;
  }
`;

const SocialLink = styled(motion.a)`
  display: inline-block;
  width: 40px;
  height: 40px;
  background: #ff6b6b;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-decoration: none;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 1.1rem;
  }
  
  &:hover {
    background: #ffd93d;
    transform: translateY(-3px);
  }
  
  &.printful-link {
    background: #1a1a1a;
    color: white;
    font-weight: bold;
    font-size: 1.6rem;
    font-family: 'Arial', sans-serif;

    @media (max-width: 768px) {
      font-size: 1.4rem;
    }
    
    &:hover {
      background: #333333;
      color: white;
    }
  }
  
  &.etsy-link {
    background: #f56400;
    color: white;
    font-weight: bold;
    font-size: 1.1rem;
    font-family: 'Georgia', serif;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
    
    &:hover {
      background: #d55a00;
      color: white;
    }
  }
  
  &.opensea-link {
    background: #2081e2;
    color: white;
    font-weight: bold;
    font-size: 0.9rem;
    font-family: 'Arial', sans-serif;
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
    }
    
    &:hover {
      background: #1a6bc7;
      color: white;
    }
  }
  
  &.ebay-link {
    background: #86d4f6;
    color: white;
    
    &:hover {
      background: #6bc4f0;
      color: white;
    }
  }
`;

const StoreButton = styled(motion.a)`
  display: inline-block;
  background: linear-gradient(45deg, #ff6b6b, #ffd93d);
  color: white;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: bold;
  margin-top: 1rem;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.95rem;
    margin-top: 0.8rem;
    width: 100%;
    text-align: center;
    display: block;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4);
  }
`;

const SitelinkDetails = styled.details`
  margin-top: 1rem;
  position: relative;
  
  summary {
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
    list-style: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;

    &::-webkit-details-marker {
      display: none;
    }

    &:after {
      content: "▼";
      font-size: 0.7rem;
      transition: transform 0.3s ease;
    }
    
    &:hover {
      background: rgba(255, 107, 107, 0.1);
      border-color: #ff6b6b;
      color: #ff6b6b;
    }
  }

  &[open] summary {
    border-radius: 0 0 4px 4px;
  }

  &[open] summary:after {
    transform: rotate(180deg);
  }

  .dropdown-content {
    position: absolute;
    bottom: 100%;
    left: 0;
    width: 100%;
    z-index: 100;
    background: #222;
    border-radius: 4px 4px 0 0;
    padding: 0.5rem 0;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-bottom: none;
    box-shadow: 0 -8px 16px rgba(0, 0, 0, 0.5);
    
    a {
      color: #ccc;
      padding: 0.5rem 1rem;
      text-decoration: none;
      display: block;
      font-size: 0.85rem;
      border-left: 2px solid transparent;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(255, 107, 107, 0.05);
        color: #ff6b6b;
        border-left-color: #ff6b6b;
      }
    }
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 2rem;
  border-top: 1px solid #555;
  color: #ccc;
  
  @media (max-width: 768px) {
    padding-top: 1.5rem;
    
    p {
      font-size: 0.9rem;
      line-height: 1.4;
    }
  }
`;

const Footer = ({ onShowComingSoon }) => {
  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <h3>🐔 Chikin Lady</h3>
          <p>
            The most cluckin' awesome brand around! 
            We bring attitude, personality, and a whole lot of fun 
            to everything we do.
          </p>
                     <SocialLinks>
                          <SocialLink
                            href="https://chikinlady.printful.me"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="printful-link"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            P
                          </SocialLink>
                          <SocialLink
               href="https://opensea.io/0x97a662d64b1ee104dd68dcb85f24f40b4c907a39"
               target="_blank"
               rel="noopener noreferrer"
               className="opensea-link"
               whileHover={{ scale: 1.1 }}
               whileTap={{ scale: 0.9 }}
             >
                               <img src="/assets/OpenSea_icon.svg.png" alt="OpenSea" width="20" height="20" />
             </SocialLink>
                         <SocialLink
              href="https://www.ebay.com/usr/chikinlady"
              target="_blank"
              rel="noopener noreferrer"
              className="ebay-link"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <img src="/assets/ebay.png" alt="eBay" width="32" height="32" />
            </SocialLink>
                         <SocialLink
              href="https://chikinlady.etsy.com"
              target="_blank"
              rel="noopener noreferrer"
              className="etsy-link"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              E
            </SocialLink>
           </SocialLinks>
        </FooterSection>
        
        <FooterSection>
          <h3>Quick Links</h3>
          <FooterLinks>
            <FooterLink
              href="#home"
              whileHover={{ x: 5 }}
            >
              Home
            </FooterLink>
            <FooterLink
              href="#products"
              whileHover={{ x: 5 }}
            >
              Products
            </FooterLink>
            <FooterLink
              href="#about"
              whileHover={{ x: 5 }}
            >
              About Us
            </FooterLink>
                         <FooterLink
               href="mailto:chikinlady@outlook.com?subject=Hey%20Chikin%20Lady"
               whileHover={{ x: 5 }}
               className="contact-link"
             >
               Contact
             </FooterLink>

             <SitelinkDetails>
               <summary>Directory & Sitelinks</summary>
                <div className="dropdown-content">
                  <Link to="/about-us">About Us</Link>
                  <Link to="/chikin-lady-shopping">Shopping & Stores</Link>
                  <Link to="/chikin-lady-appstore">Appstore (Coming Soon)</Link>
                  <Link to="/farm-chicken-apparel">Farm & Chicken Apparel</Link>
                  <Link to="/chikin-web-games">Chicken Web Games</Link>
                  <Link to="/chicken-gifts-texas">Texas Store</Link>
                  <Link to="/chicken-gifts-florida">Florida Store</Link>
                  <Link to="/chicken-gifts-california">California Store</Link>
                  <a href="/sitemap.xml">Sitemap</a>
                </div>
             </SitelinkDetails>
          </FooterLinks>
        </FooterSection>
        
        <FooterSection>
          <h3>Shop</h3>
          <p>
            Ready to rock some Chikin Lady gear? 
            Check out our latest collection and rock your feathers!
          </p>
          <StoreButton
            href="https://chikinlady.printful.me/"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            🛒 Visit Store
          </StoreButton>
        </FooterSection>
        
        <FooterSection>
          <h3>🚀 Coming Soon</h3>
          <p>
            Get ready for the most egg-citing web app launch! 
            Chikin Lady web app is on the horizon.
          </p>
          <StoreButton
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (onShowComingSoon) {
                onShowComingSoon();
              }
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ background: 'linear-gradient(45deg, #6bcf7f, #4ecdc4)' }}
          >
            📱 GET APP
          </StoreButton>
        </FooterSection>
      </FooterContent>
      
      <Copyright>
        <p>&copy; 2024 Chikin Lady. All rights reserved. | Made with 🐔 and ❤️</p>
      </Copyright>
    </FooterContainer>
  );
};

export default Footer;
